import { useState, useEffect } from 'react';
import { Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader } from './api';

export function IdeasManagement() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState(null);

  useEffect(() => { fetchIdeas(); }, []);

  const fetchIdeas = async () => {
    try { const response = await axios.get(`${API}/ideas`, { headers: getAuthHeader() }); setIdeas(response.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/ideas/${id}/read`, {}, { headers: getAuthHeader() });
      fetchIdeas();
    } catch { toast.error('Failed to update'); }
  };

  const deleteIdea = async (id) => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      await axios.delete(`${API}/ideas/${id}`, { headers: getAuthHeader() });
      toast.success('Idea deleted');
      setSelectedIdea(null);
      fetchIdeas();
    } catch { toast.error('Failed to delete'); }
  };

  const viewIdea = async (idea) => {
    setSelectedIdea(idea);
    if (!idea.is_read) {
      await markRead(idea.id);
    }
  };

  const unreadCount = ideas.filter(i => !i.is_read).length;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">Startup Ideas</h1>
          {unreadCount > 0 && <p className="text-sm text-[#C83041] mt-1">{unreadCount} new</p>}
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Idea Title</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ideas.map((idea) => (
              <TableRow key={idea.id} className={`cursor-pointer hover:bg-gray-50 ${!idea.is_read ? 'bg-amber-50/50' : ''}`} onClick={() => viewIdea(idea)}>
                <TableCell className="font-medium">{idea.name}</TableCell>
                <TableCell>
                  <p className="text-sm">{idea.email}</p>
                  <p className="text-xs text-gray-500">{idea.phone}</p>
                </TableCell>
                <TableCell className="font-semibold max-w-[200px] truncate">{idea.idea_title}</TableCell>
                <TableCell className="text-sm text-gray-500">{formatDate(idea.submitted_at)}</TableCell>
                <TableCell>
                  <Badge className={idea.is_read ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'}>
                    {idea.is_read ? 'Reviewed' : 'New'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => viewIdea(idea)} title="View details">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteIdea(idea.id)} title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {ideas.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#58595B] py-8">No ideas submitted yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Idea Detail Modal */}
      <Dialog open={!!selectedIdea} onOpenChange={() => setSelectedIdea(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">{selectedIdea?.idea_title}</DialogTitle>
            <DialogDescription>Submitted by {selectedIdea?.name}</DialogDescription>
          </DialogHeader>
          {selectedIdea && (
            <div className="space-y-5 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Name</Label>
                  <p className="font-medium">{selectedIdea.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Submitted</Label>
                  <p className="text-sm">{formatDate(selectedIdea.submitted_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <p className="text-sm">{selectedIdea.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone</Label>
                  <p className="text-sm">{selectedIdea.phone}</p>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Description</Label>
                <p className="mt-1 text-[#231F20] whitespace-pre-wrap leading-relaxed">{selectedIdea.description}</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedIdea(null)}>Close</Button>
                <Button variant="destructive" className="flex-1" onClick={() => deleteIdea(selectedIdea.id)}>Delete Idea</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
